import type { PluginDefinition, PluginManifestEntry } from './types';
import { pluginRegistry } from '@/plugins/pluginRegistry';
import { APP_VERSION } from '@/app/version';

export interface PluginBootstrapOptions {
  plugins: PluginDefinition[];
  manifest?: PluginManifestEntry[];
  hostVersion?: string;
  hostCapabilities?: string[];
  enabledPluginIds?: string[];
}

export async function bootstrapPlugins(options: PluginBootstrapOptions) {
  const { plugins, hostCapabilities, hostVersion } = options;
  pluginRegistry.configure({
    hostCapabilities: hostCapabilities ?? [],
    hostVersion: hostVersion ?? APP_VERSION,
  });

  const manifestPlugins = await loadManifestPlugins(options.manifest);
  const allPlugins = mergePlugins([...plugins, ...manifestPlugins]);
  allPlugins.forEach((plugin) => pluginRegistry.register(plugin));

  const enabledIds = resolveEnabledPlugins({
    plugins: allPlugins,
    manifest: options.manifest,
    enabledPluginIds: options.enabledPluginIds,
  });

  pluginRegistry.setEnabledPluginIds(enabledIds);
  pluginRegistry.initializeAll();

  return enabledIds;
}

export async function syncServerEnablement(enabledIds: string[]) {
  void enabledIds;
  // Placeholder for server-config sync (to be wired to backend endpoint).
}

interface ResolveEnabledOptions {
  plugins: PluginDefinition[];
  manifest?: PluginManifestEntry[];
  enabledPluginIds?: string[];
}

async function loadManifestPlugins(manifest?: PluginManifestEntry[]) {
  if (!manifest || manifest.length === 0) {
    return [] as PluginDefinition[];
  }

  const entries = manifest.filter((entry) => Boolean(entry.bundleUrl));
  const loaded: PluginDefinition[] = [];

  // In dev mode, map known plugin IDs to their source modules so they share
  // the same React instance served by Vite's dev server.  Production builds
  // use the bundleUrl which references shared vendor chunks.
  const devLoaders: Record<string, () => Promise<unknown>> = import.meta.env.DEV
    ? {
        'knowledge-capture': () => import('@/plugins/external/knowledgeCapturePlugin'),
      }
    : {};

  await Promise.allSettled(
    entries.map(async (entry) => {
      if (!entry.bundleUrl) {
        return;
      }

      try {
        const devLoader = devLoaders[entry.id];
        const module = devLoader
          ? await devLoader()
          : await import(/* @vite-ignore */ entry.bundleUrl);
        const candidate = (module as { default?: unknown; plugin?: unknown }).default
          ?? (module as { plugin?: unknown }).plugin
          ?? module;
        if (!candidate || typeof candidate !== 'object') {
          throw new Error('Invalid plugin bundle export.');
        }

        const definition = candidate as Partial<PluginDefinition>;
        const plugin: PluginDefinition = {
          id: entry.id,
          name: definition.name ?? entry.name ?? entry.id,
          version: definition.version ?? entry.version ?? APP_VERSION,
          description: definition.description ?? entry.description ?? '',
          internal: entry.internal ?? definition.internal,
          requiredCapabilities: entry.requiredCapabilities ?? definition.requiredCapabilities,
          hostVersion: definition.hostVersion ?? (entry.minHostVersion ? { min: entry.minHostVersion } : undefined),
          requires: entry.requires ?? definition.requires,
          initializationOrder: entry.initializationOrder ?? definition.initializationOrder,
          initialize: definition.initialize ?? (() => undefined),
          navigation: definition.navigation,
          slots: definition.slots,
          routes: definition.routes,
        };

        loaded.push(plugin);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load plugin bundle.';
        pluginRegistry.register({
          id: entry.id,
          name: entry.name ?? entry.id,
          version: entry.version ?? APP_VERSION,
          description: entry.description ?? 'Plugin failed to load.',
          internal: entry.internal,
          initialize: () => {
            throw new Error(message);
          },
        });
      }
    })
  );

  return loaded;
}

/**
 * Dynamically load a plugin bundle from a manifest entry, register it in the
 * plugin registry and optionally enable it. Call this after a Marketplace
 * install so the user can immediately toggle the plugin without a full reload.
 */
export async function loadAndRegisterPlugin(
  entry: PluginManifestEntry,
  options?: { enable?: boolean }
) {
  const loaded = await loadManifestPlugins([entry]);
  if (loaded.length === 0) {
    return;
  }
  const plugin = loaded[0];
  pluginRegistry.register(plugin);

  if (options?.enable) {
    if (!pluginRegistry.isPluginEnabled(plugin.id)) {
      pluginRegistry.togglePlugin(plugin.id);
    }
  }
}

function mergePlugins(plugins: PluginDefinition[]) {
  const byId = new Map<string, PluginDefinition>();
  plugins.forEach((plugin) => {
    byId.set(plugin.id, plugin);
  });
  return Array.from(byId.values());
}

function resolveEnabledPlugins(options: ResolveEnabledOptions) {
  const { plugins, manifest, enabledPluginIds } = options;
  const pluginIds = plugins.map((plugin) => plugin.id);

  let baseIds: string[];
  if (enabledPluginIds && enabledPluginIds.length > 0) {
    baseIds = enabledPluginIds;
  } else if (manifest && manifest.length > 0) {
    const enabledByDefault = manifest
      .filter((entry) => entry.enabledByDefault)
      .map((entry) => entry.id);
    baseIds = enabledByDefault.length > 0 ? enabledByDefault : pluginIds;
  } else {
    baseIds = pluginIds;
  }

  if (enabledPluginIds && enabledPluginIds.length > 0 && manifest && manifest.length > 0) {
    const requiredByInternal = new Set(
      plugins
        .filter((plugin) => plugin.internal)
        .flatMap((plugin) => plugin.requires ?? [])
    );
    const enabledByDefault = manifest
      .filter((entry) => entry.enabledByDefault && requiredByInternal.has(entry.id))
      .map((entry) => entry.id);
    if (enabledByDefault.length > 0) {
      baseIds = Array.from(new Set([...baseIds, ...enabledByDefault]));
    }
  }

  if (!enabledPluginIds || enabledPluginIds.length === 0) {
    const internalIds = plugins.filter((plugin) => plugin.internal).map((plugin) => plugin.id);
    baseIds = Array.from(new Set([...baseIds, ...internalIds]));
  }

  const resolved = new Set<string>();
  const pluginById = new Map(plugins.map((plugin) => [plugin.id, plugin]));

  const visit = (pluginId: string) => {
    const plugin = pluginById.get(pluginId);
    if (!plugin) {
      return;
    }
    if (resolved.has(pluginId)) {
      return;
    }
    resolved.add(pluginId);
    (plugin.requires ?? []).forEach(visit);
  };

  baseIds.forEach(visit);

  // Enable internal plugins whose dependencies are now resolved
  // Keep iterating until no new plugins are added (to handle chains of dependencies)
  let addedNew = true;
  while (addedNew) {
    addedNew = false;
    plugins.forEach((plugin) => {
      if (resolved.has(plugin.id)) {
        return; // Already resolved
      }
      if (!plugin.internal || !plugin.requires || plugin.requires.length === 0) {
        return;
      }
      // Check if all dependencies are resolved
      const allDepsResolved = plugin.requires.every((depId) => resolved.has(depId));
      if (allDepsResolved) {
        visit(plugin.id);
        addedNew = true;
      }
    });
  }

  return Array.from(resolved);
}
